export type ThemePreference = 'auto' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

export const DEFAULT_THEME_PREFERENCE: ThemePreference = 'auto';
export const LIGHT_THEME_COLOR = '#f7f2e7';
export const DARK_THEME_COLOR = '#090b0c';

export function resolveTheme(
  preference: ThemePreference,
  currentTime: Date,
): ResolvedTheme {
  if (preference !== 'auto') return preference;
  const hour = currentTime.getHours();
  return hour >= 6 && hour < 17 ? 'light' : 'dark';
}

export function millisecondsUntilNextThemeBoundary(currentTime: Date): number {
  const next = new Date(currentTime.getTime());
  const hour = currentTime.getHours();
  if (hour < 6) {
    next.setHours(6, 0, 0, 0);
  } else if (hour < 17) {
    next.setHours(17, 0, 0, 0);
  } else {
    next.setDate(next.getDate() + 1);
    next.setHours(6, 0, 0, 0);
  }
  return Math.max(1, next.getTime() - currentTime.getTime());
}

export interface ThemeEventTarget {
  addEventListener(type: 'visibilitychange', listener: () => void): void;
  removeEventListener(type: 'visibilitychange', listener: () => void): void;
}

export interface ThemeRuntime {
  now(): Date;
  setTimer(listener: () => void, delayMs: number): unknown;
  clearTimer(timer: unknown): void;
  visibility: ThemeEventTarget;
  isVisible(): boolean;
}

export interface ThemeStore {
  getSnapshot(): ResolvedTheme;
  getServerSnapshot(): ResolvedTheme;
  subscribe(listener: () => void): () => void;
}

export function createThemeStore(
  runtime: ThemeRuntime,
  preference: ThemePreference = DEFAULT_THEME_PREFERENCE,
): ThemeStore {
  let current = resolveTheme(preference, runtime.now());
  let timer: unknown;
  const listeners = new Set<() => void>();

  const schedule = (): void => {
    if (timer !== undefined) runtime.clearTimer(timer);
    timer = undefined;
    if (preference === 'auto' && listeners.size > 0) {
      timer = runtime.setTimer(refresh, millisecondsUntilNextThemeBoundary(runtime.now()));
    }
  };

  const refresh = (): void => {
    const next = resolveTheme(preference, runtime.now());
    if (next !== current) {
      current = next;
      listeners.forEach((listener) => listener());
    }
    schedule();
  };

  const onVisibilityChange = (): void => {
    if (runtime.isVisible()) refresh();
  };

  return {
    getSnapshot: () => current,
    getServerSnapshot: () => current,
    subscribe(listener) {
      listeners.add(listener);
      if (listeners.size === 1) {
        runtime.visibility.addEventListener('visibilitychange', onVisibilityChange);
        refresh();
      }
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) {
          runtime.visibility.removeEventListener(
            'visibilitychange',
            onVisibilityChange,
          );
          if (timer !== undefined) runtime.clearTimer(timer);
          timer = undefined;
        }
      };
    },
  };
}

const fallbackVisibility: ThemeEventTarget = {
  addEventListener: () => undefined,
  removeEventListener: () => undefined,
};

export const browserThemeStore = createThemeStore({
  now: () => new Date(),
  setTimer: (listener, delayMs) => window.setTimeout(listener, delayMs),
  clearTimer: (timer) => window.clearTimeout(timer as number),
  visibility: typeof document === 'undefined' ? fallbackVisibility : document,
  isVisible: () =>
    typeof document === 'undefined' || document.visibilityState === 'visible',
});

export function applyThemeToDocument(
  theme: ResolvedTheme,
  documentRoot: HTMLElement = document.documentElement,
  themeColorMeta: HTMLMetaElement | null = document.querySelector(
    'meta[name="theme-color"]',
  ),
): void {
  documentRoot.dataset.theme = theme;
  documentRoot.style.colorScheme = theme;
  themeColorMeta?.setAttribute(
    'content',
    theme === 'light' ? LIGHT_THEME_COLOR : DARK_THEME_COLOR,
  );
}
